import type { EvolutionSoundKey } from './evolutionCues';
import type { PhaseTransitionKey } from './phaseCues';

export type RewardSoundKey = EvolutionSoundKey | PhaseTransitionKey;

type Voice = { source: AudioBufferSourceNode; gain: GainNode; end: number };

/** A bounded, independently mixed pool keeps rapid physics merges from clipping. */
export class EvolutionSoundPlayer {
  private readonly output: GainNode;
  private readonly compressor: DynamicsCompressorNode;
  private readonly voices = new Set<Voice>();
  private readonly retiring = new Set<Voice>();
  private readonly lastPlayed = new Map<RewardSoundKey, number>();
  private destroyed = false;

  constructor(private readonly context: AudioContext, destination: AudioNode,
    private readonly getBuffer: (key: RewardSoundKey) => AudioBuffer | undefined) {
    this.output = context.createGain();
    this.compressor = context.createDynamicsCompressor();
    this.compressor.threshold.value = -12;
    this.compressor.knee.value = 12;
    this.compressor.ratio.value = 4;
    this.compressor.attack.value = 0.003;
    this.compressor.release.value = 0.16;
    this.compressor.connect(this.output);
    this.output.connect(destination);
  }

  setVolume(volume: number, muted: boolean): void {
    const now = this.context.currentTime;
    this.output.gain.cancelScheduledValues(now);
    this.output.gain.setTargetAtTime(muted ? 0 : volume, now, 0.008);
  }

  play(key: RewardSoundKey, volume: number, delay = 0): boolean {
    if (this.destroyed) return false;
    const buffer = this.getBuffer(key);
    if (!buffer) return false;
    const now = this.context.currentTime;
    const spacing = 0.065;
    if (now - (this.lastPlayed.get(key) ?? -Infinity) < spacing) return true;
    this.lastPlayed.set(key, now);
    for (const voice of this.voices) {
      if (voice.end <= now) this.release(voice);
    }
    // Retire the oldest tail with a short fade, never cut an audible waveform.
    if (this.voices.size >= 6) {
      const oldest = this.voices.values().next().value!;
      oldest.gain.gain.cancelScheduledValues(now);
      oldest.gain.gain.setTargetAtTime(0, now, 0.004);
      oldest.source.stop(now + 0.025);
      this.voices.delete(oldest);
      this.retiring.add(oldest);
    }
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = buffer;
    gain.gain.value = volume;
    source.connect(gain);
    gain.connect(this.compressor);
    const voice = { source, gain, end: now + delay + buffer.duration };
    source.onended = () => this.release(voice);
    this.voices.add(voice);
    source.start(now + delay);
    return true;
  }

  private release(voice: Voice): void {
    voice.source.disconnect();
    voice.gain.disconnect();
    this.voices.delete(voice);
    this.retiring.delete(voice);
  }

  destroy(): void {
    this.destroyed = true;
    for (const voice of [...this.voices, ...this.retiring]) {
      voice.source.stop();
      this.release(voice);
    }
    this.compressor.disconnect();
    this.output.disconnect();
  }
}
